import { useParams } from "react-router-dom";
import { Link } from "../components/LocaleLink";
import { ArrowRight, ChevronRight, Check } from "lucide-react";
import { getSeoPage } from "../data/seoPages";
import { useLang } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { track } from "../lib/analytics";
import { JsonLd, buildFaqSchema, buildBreadcrumbSchema, buildServiceSchema } from "../components/JsonLd";
import { renderInline, stripMarkdown } from "../components/RichText";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const origin = () => (typeof window !== "undefined" ? window.location.origin : "https://velmiraliving.com");

export default function LandingPage() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const page = getSeoPage(slug, lang);

  if (!page) {
    return (
      <div className="pt-44 pb-32 text-center" data-testid="landing-notfound">
        <Seo title="Page not found | Velmira Living" description="This page does not exist." noindex />
        <p className="font-headings text-3xl text-foreground">{t("landing.notFound")}</p>
        <Link to="/" className="mt-6 inline-block text-primary uppercase tracking-widest text-xs">{t("landing.backHome")}</Link>
      </div>
    );
  }

  const url = `${origin()}/${lang}/${slug}`;
  const isAirbnb = slug.includes("airbnb");
  const ownerCta = { to: "/owner", leadType: "owner", title: "landing.ownerCtaTitle", text: "landing.ownerCtaText", button: "landing.ownerCtaButton",
    onClick: () => track(isAirbnb ? "airbnb_management_cta_click" : "property_management_cta_click", { cta_location: "landing_page", lead_type: "owner" }) };
  const buyerCta = (to, button) => ({ to, leadType: "buyer", title: "landing.buyerCtaTitle", text: "landing.buyerCtaText", button,
    onClick: () => track("buyer_cta_click", { cta_location: "landing_page", lead_type: "buyer" }) });
  const cta = { manage: ownerCta, rent: ownerCta, concierge: ownerCta, buy: buyerCta("/buy", "landing.buyerCtaButton"), invest: buyerCta("/invest", "landing.investCtaButton"), valuation: ownerCta }[page.ctaInterest]
    || { to: `/contact?interest=${page.ctaInterest}`, leadType: "general", title: "landing.ctaTitle", text: "landing.ctaText", button: "landing.ctaButton", onClick: undefined };
  const breadcrumb = [
    { name: t("landing.home"), url: `${origin()}/${lang}` },
    { name: page.h1, url },
  ];

  return (
    <div data-testid="landing-page" className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo title={page.title} description={page.description} />
      <JsonLd id="landing-service" data={buildServiceSchema(page.h1, page.description, url)} />
      {page.faq?.length ? <JsonLd id="landing-faq" data={buildFaqSchema(page.faq.map((f) => ({ q: f.q, a: stripMarkdown(f.a) })))} /> : null}
      <JsonLd id="landing-breadcrumb" data={buildBreadcrumbSchema(breadcrumb)} />

      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1100px]">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" data-testid="breadcrumb" className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-8">
          <Link to="/" className="hover:text-primary">{t("landing.home")}</Link>
          <ChevronRight size={12} />
          <span className="text-primary">{page.category}</span>
        </nav>

        <Reveal>
          <h1 className="font-headings text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-4xl">{page.h1}</h1>
          <div className="mt-8 space-y-5 font-body font-light text-lg text-foreground/75 leading-relaxed max-w-3xl">
            {page.intro.map((para, i) => <p key={i}>{renderInline(para)}</p>)}
          </div>
        </Reveal>

        {/* Sections */}
        <div className="mt-16 space-y-14">
          {page.sections.map((s, i) => (
            <Reveal key={i}>
              <section>
                <h2 className="font-headings text-3xl sm:text-4xl leading-tight text-foreground">{s.h2}</h2>
                {s.body?.map((para, j) => (
                  <p key={j} className="mt-5 font-body font-light text-lg text-foreground/75 leading-relaxed max-w-3xl">{renderInline(para)}</p>
                ))}
                {s.bullets?.length ? (
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl">
                    {s.bullets.map((b, k) => (
                      <li key={k} className="flex items-start gap-3 font-body font-light text-foreground/80">
                        <Check size={18} className="text-primary shrink-0 mt-1" /> {b}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </section>
            </Reveal>
          ))}
        </div>

        {/* FAQ */}
        {page.faq?.length ? (
          <Reveal>
            <section className="mt-20">
              <h2 className="font-headings text-3xl sm:text-4xl leading-tight text-foreground mb-8">{t("landing.faqTitle")}</h2>
              <Accordion type="single" collapsible className="w-full" data-testid="landing-faq">
                {page.faq.map((f, i) => (
                  <AccordionItem key={i} value={`lp-faq-${i}`} className="border-border/60">
                    <AccordionTrigger data-testid={`landing-faq-trigger-${i}`} className="font-headings text-xl md:text-2xl text-foreground hover:text-primary hover:no-underline text-left py-6">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="font-body font-light text-foreground/70 leading-relaxed text-base pb-6">
                      {renderInline(f.a)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </Reveal>
        ) : null}

        {/* CTA */}
        <Reveal>
          <div data-testid="landing-cta" data-lead-type={cta.leadType} className="mt-20 bg-secondary border border-border/60 p-10 md:p-14 text-center">
            <h2 className="font-headings text-3xl sm:text-4xl leading-tight text-foreground">{t(cta.title)}</h2>
            <p className="mt-4 font-body font-light text-foreground/70 max-w-xl mx-auto">{t(cta.text)}</p>
            <Link to={cta.to} onClick={cta.onClick} data-testid="landing-cta-button" className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
              {t(cta.button)} <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>

        {/* Related (internal linking) */}
        {page.related?.length ? (
          <div className="mt-16">
            <h3 className="font-accent text-xs uppercase tracking-[0.2em] text-primary mb-6">{t("landing.relatedTitle")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {page.related.map((rSlug) => {
                const r = getSeoPage(rSlug, lang);
                if (!r) return null;
                return (
                  <Link key={rSlug} to={`/${rSlug}`} data-testid={`related-${rSlug}`} className="group border border-border/50 p-6 hover:border-primary transition-colors duration-300">
                    <span className="font-headings text-xl text-foreground group-hover:text-primary transition-colors">{r.h1}</span>
                    <span className="mt-2 block font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{t("landing.readMore")}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
